import {Component, ElementRef, OnDestroy, OnInit, ViewChild, effect} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {finalize, Subject, takeUntil} from 'rxjs';
import * as signalR from '@microsoft/signalr';
import {ChatService} from '../../../../Services/chat.service';
import {ConfirmDialogService} from '../../../../Services/confirm-dialog.service';
import {ChatMessage, ChatParticipant, ChatThread, ChatUser} from './chat-thread.model';

@Component({
  selector: 'app-chat-thread',
  templateUrl: './chat-thread.component.html',
  styleUrls: ['./chat-thread.component.scss'],
  standalone: false
})
export class ChatThreadComponent implements OnInit, OnDestroy {
  @ViewChild('attachmentInput') attachmentInput?: ElementRef<HTMLInputElement>;
  @ViewChild('conversationScroller', {read: ElementRef}) conversationScroller?: ElementRef<HTMLElement>;
  @ViewChild('messageInput', {read: ElementRef}) messageInput?: ElementRef<HTMLTextAreaElement>;

  currentUserId = '';
  currentUserName = '';

  threads: ChatThread[] = [];
  selectedThreadId = '';
  lastOpenedThreadId = '';
  selectedMessages: ChatMessage[] = [];
  typingUsers: string[] = [];
  availableUsers: ChatUser[] = [];
  selectedRecipientUserIds: string[] = [];
  userSelectionIntent: 'new' | 'add' | null = null;
  newConversationTitle = '';
  isCreatingNewConversation = false;
  userSearchTerm = '';
  threadSearchTerm = '';
  hasChats = false;

  newMessage = '';
  selectedAttachment: File | null = null;
  attachmentError = '';
  attachmentErrorFileName = '';
  submitted = false;
  deletingThread = false;
  startingConversation = false;
  updatingParticipants = false;
  showEmojiPicker = false;
  readonly emojiOptions: string[] = ['😀', '😂', '😊', '👍', '🙏', '🎉', '❤️', '🔥', '😍', '😁', '👏', '🤝', '🙌', '😎', '🤔', '💯', '✅', '🚀'];
  private readonly maxAttachmentSizeBytes = 5 * 1024 * 1024;
  private readonly blockedAttachmentExtensions = new Set([
    'exe', 'scr', 'bat', 'cmd', 'com', 'msi', 'msp', 'ps1', 'psm1', 'js', 'jse', 'vbs', 'vbe', 'wsf', 'wsh',
    'jar', 'sh', 'bash', 'zsh', 'ksh', 'csh', 'app', 'gadget', 'msix', 'msixbundle', 'reg'
  ]);

  private readonly destroy$ = new Subject<void>();
  private typingTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly messageSearchLoadedThreadIds = new Set<string>();
  private readonly syncThreadsEffect = effect(() => {
    const threads = this.chatService.threads();
    this.threads = threads;
    this.hasChats = threads.length > 0;

    if (!this.selectedThreadId) {
      this.isCreatingNewConversation = !this.hasChats;
    }

    if (this.threadSearchTerm.trim()) {
      this.onThreadSearchTermChange();
    }

    if (!this.selectedThreadId && !this.isCreatingNewConversation && threads.length > 0) {
      const firstValidThread = threads.find((thread) => this.isValidThreadId(thread?.id));

      if (firstValidThread?.id) {
        this.openThread(firstValidThread.id);
      }
    }

    this.updateTabTitle();
  });
  private readonly syncMessagesEffect = effect(() => {
    const messagesByThread = this.chatService.messages();
    this.selectedMessages = messagesByThread[this.selectedThreadId] || [];

    if (this.selectedThreadId) {
      this.scrollConversationToBottom();
    }
  });
  private readonly syncTypingEffect = effect(() => {
    const typingByThread = this.chatService.typing();
    const threadTyping = typingByThread[this.selectedThreadId] || [];
    this.typingUsers = threadTyping.filter((state) => state.userId !== this.currentUserId).map((state) => state.displayName);
  });

  constructor(
    private chatService: ChatService,
    private route: ActivatedRoute,
    private router: Router,
    private confirmDialogService: ConfirmDialogService,
    private titleService: Title
  ) {
  }

  ngOnInit(): void {
    this.updateTabTitle();

    const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
    this.currentUserId = (currentUser?.id ?? currentUser?.userId ?? '').toString();
    this.currentUserName = currentUser?.name || currentUser?.username || 'Current User';
    void this.chatService.connectRealtime(this.currentUserId);

    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const threadId = params.get('threadId');
      if (this.isValidThreadId(threadId)) {
        this.isCreatingNewConversation = false;
        this.selectedThreadId = threadId;
        this.chatService.setActiveThread(threadId);
        this.loadThread(threadId);
      } else {
        this.chatService.setActiveThread('');
        this.selectedThreadId = '';
        this.selectedMessages = [];
        this.updateTabTitle();
      }
    });

    this.chatService.loadThreads(this.currentUserId);
    this.loadAvailableUsers();
  }

  get selectedThread(): ChatThread | undefined {
    return this.threads.find((thread) => thread.id === this.selectedThreadId);
  }

  get canCompose(): boolean {
    return this.isHubConnected && (!!this.selectedThreadId || this.selectedRecipientUserIds.length > 0);
  }

  get isHubConnected(): boolean {
    return this.chatService.isRealtimeConnected();
  }

  get hubStatusLabel(): string {
    const state = this.chatService.realtimeConnectionStateReadonly();
    return state === signalR.HubConnectionState.Connected ? 'Connected' : 'Disconnected';
  }

  get chatInfoAvatarUrl(): string {
    if (this.isCreatingNewConversation && this.selectedRecipientUserIds.length > 0) {
      const firstSelectedUser = this.availableUsers.find((user) => user.userId === this.selectedRecipientUserIds[0]);
      return this.chatService.resolveAvatarUrl(firstSelectedUser?.avatarUrl) || 'assets/images/users/avatar-2.jpg';
    }

    const otherParticipant = this.selectedThread?.participants?.find((participant) => participant.userId !== this.currentUserId);
    return this.chatService.resolveAvatarUrl(otherParticipant?.avatarUrl || this.selectedThread?.lastMessage?.senderAvatarUrl) || 'assets/images/users/avatar-2.jpg';
  }

  get chatInfoDisplayName(): string {
    if (this.isCreatingNewConversation && this.selectedRecipientUserIds.length > 0) {
      const selectedUsers = this.availableUsers.filter((user) => this.selectedRecipientUserIds.includes(user.userId));
      const names = selectedUsers.map((user) => user.name).filter(Boolean);

      if (names.length === 0) {
        return 'New conversation';
      }

      if (names.length <= 3) {
        return names.join(', ');
      }

      return `${names.slice(0, 2).join(', ')} and ${names.length - 2} others`;
    }

    const participantNames = (this.selectedThread?.participants || [])
      .filter((participant) => participant.userId !== this.currentUserId)
      .map((participant) => participant.name)
      .filter(Boolean);

    if (participantNames.length === 0) {
      return this.selectedThread?.title || 'No conversation selected';
    }

    if (participantNames.length <= 3) {
      return participantNames.join(', ');
    }

    return `${participantNames.slice(0, 2).join(', ')} and ${participantNames.length - 2} others`;
  }

  get chatInfoParticipants(): ChatParticipant[] {
    if (this.selectedThreadId) {
      return (this.selectedThread?.participants || []).filter((participant) => participant.userId !== this.currentUserId);
    }

    if (this.selectedRecipientUserIds.length > 0) {
      return this.availableUsers
        .filter((user) => this.selectedRecipientUserIds.includes(user.userId))
        .map((user) => ({
          userId: user.userId,
          name: user.name,
          avatarUrl: user.avatarUrl
        }));
    }

    return [];
  }

  get selectedThreadMessageCount(): number {
    return this.selectedMessages.length;
  }

  get selectedThreadAttachmentCount(): number {
    return this.selectedMessages.filter((message) => !!message.attachmentFileName).length;
  }

  get selectedThreadParticipantCount(): number {
    return this.selectedThread?.participants?.length || 0;
  }

  get selectedThreadLastActivity(): string {
    return this.selectedMessages[this.selectedMessages.length - 1]?.createdAt || this.selectedThread?.lastMessage?.createdAt || '';
  }

  get filteredAvailableUsers(): ChatUser[] {
    const normalizedTerm = this.userSearchTerm.trim().toLowerCase();
    const existingParticipantIds = new Set((this.selectedThread?.participants || []).map((participant) => participant.userId));
    const shouldFilterExistingParticipants = this.userSelectionIntent === 'add' && !!this.selectedThreadId;
    const usersNotInThread = shouldFilterExistingParticipants
      ? this.availableUsers.filter((user) => !existingParticipantIds.has(user.userId))
      : this.availableUsers;

    if (!normalizedTerm) {
      return usersNotInThread;
    }

    return usersNotInThread.filter((user) => {
      const name = (user.name || '').toLowerCase();
      const userId = (user.userId || '').toLowerCase();
      return name.includes(normalizedTerm) || userId.includes(normalizedTerm);
    });
  }

  get filteredThreads(): ChatThread[] {
    const normalizedTerm = this.threadSearchTerm.trim().toLowerCase();

    if (!normalizedTerm) {
      return this.threads;
    }

    const messagesByThread = this.chatService.messages();

    return this.threads.filter((thread) => {
      const title = (thread.title || '').toLowerCase();
      const allMessages = messagesByThread[thread.id] || [];
      const hasMatchingMessage = allMessages.some((message) => {
        const body = (message.body || '').toLowerCase();
        const senderName = (message.senderName || '').toLowerCase();
        const attachmentFileName = (message.attachmentFileName || '').toLowerCase();

        return body.includes(normalizedTerm)
          || senderName.includes(normalizedTerm)
          || attachmentFileName.includes(normalizedTerm);
      });

      return title.includes(normalizedTerm)
        || hasMatchingMessage;
    });
  }

  onThreadSearchTermChange(): void {
    const normalizedTerm = this.threadSearchTerm.trim();
    if (!normalizedTerm || !this.currentUserId) {
      return;
    }

    for (const thread of this.threads) {
      if (!this.isValidThreadId(thread?.id) || this.messageSearchLoadedThreadIds.has(thread.id)) {
        continue;
      }

      this.messageSearchLoadedThreadIds.add(thread.id);
      this.chatService.loadMessages(thread.id, this.currentUserId);
    }
  }

  openThread(threadId: string): void {
    if (!this.isValidThreadId(threadId)) {
      return;
    }

    this.lastOpenedThreadId = threadId;
    this.selectedRecipientUserIds = [];
    this.userSelectionIntent = null;
    this.newConversationTitle = '';
    this.isCreatingNewConversation = false;
    this.showEmojiPicker = false;
    this.selectedThreadId = threadId;
    this.updateTabTitle();
    this.router.navigate(['/chat', threadId]).then();
  }

  // beginNewConversation(): void {
  //   this.selectedThreadId = '';
  //   this.lastOpenedThreadId = '';
  //   this.selectedMessages = [];
  //   this.typingUsers = [];
  //   this.selectedRecipientUserIds = [];
  //   this.userSelectionIntent = 'new';
  //   this.newConversationTitle = '';
  //   this.userSearchTerm = '';
  //   this.isCreatingNewConversation = true;
  //   this.showEmojiPicker = false;
  //   this.updateTabTitle();
  //   this.router.navigate(['/chat']);
  // }

  showUserSelection(intent: 'new' | 'add'): void {
    if (intent === 'add' && !this.selectedThreadId) {
      return;
    }

    this.userSelectionIntent = intent;
    this.isCreatingNewConversation = true;
    this.selectedRecipientUserIds = [];
    this.userSearchTerm = '';

    if (intent === 'new') {
      this.newConversationTitle = '';
    }
  }

  selectRecipient(userId: string): void {
    if (!userId || this.startingConversation || this.updatingParticipants) {
      return;
    }

    if (this.selectedRecipientUserIds.includes(userId)) {
      this.selectedRecipientUserIds = this.selectedRecipientUserIds.filter((id) => id !== userId);
      return;
    }

    this.selectedRecipientUserIds = [...this.selectedRecipientUserIds, userId];
  }

  // applyUserSelection(): void {
  //   if (this.userSelectionIntent === 'add') {
  //     this.addParticipantsToSelectedThread();
  //     return;
  //   }
  //
  //   this.startConversation();
  // }

  startConversation(): void {
    if (!this.currentUserId || this.selectedRecipientUserIds.length === 0 || this.startingConversation) {
      return;
    }

    const selectedRecipientUserIds = this.selectedRecipientUserIds.filter((userId) =>
      this.availableUsers.some((user) => user.userId === userId)
    );

    if (selectedRecipientUserIds.length === 0) {
      return;
    }

    this.startingConversation = true;
    const conversationTitle = this.newConversationTitle.trim();

    this.chatService
      .createOrGetThreadForParticipants(this.currentUserId, selectedRecipientUserIds, conversationTitle)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.startingConversation = false;
        })
      )
      .subscribe((thread) => {
        if (!thread?.id) {
          return;
        }

        this.isCreatingNewConversation = false;
        this.userSelectionIntent = null;
        this.selectedRecipientUserIds = [];
        this.newConversationTitle = '';
        this.openThread(thread.id);
      });
  }

  addParticipantsToSelectedThread(): void {
    if (!this.selectedThreadId || !this.currentUserId || this.selectedRecipientUserIds.length === 0 || this.updatingParticipants) {
      return;
    }

    this.updatingParticipants = true;
    this.chatService
      .addParticipants(this.selectedThreadId, this.currentUserId, this.selectedRecipientUserIds)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.updatingParticipants = false;
        })
      )
      .subscribe((thread) => {
        if (!thread?.id) {
          return;
        }

        this.isCreatingNewConversation = false;
        this.userSelectionIntent = null;
        this.selectedRecipientUserIds = [];
        this.userSearchTerm = '';
      });
  }

  loadThread(threadId: string): void {
    if (!this.currentUserId || !threadId) {
      return;
    }

    this.chatService.loadThread(threadId, this.currentUserId);
    this.chatService.loadMessages(threadId, this.currentUserId);
    this.chatService.markThreadAsRead(threadId, this.currentUserId);
    this.scrollConversationToBottom();
  }

  private scrollConversationToBottom(): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const host = this.conversationScroller?.nativeElement;
        if (!host) {
          return;
        }

        const simplebarWrapper = host.querySelector('.simplebar-content-wrapper') as HTMLElement | null;
        const scrollTarget = simplebarWrapper ?? host;
        scrollTarget.scrollTop = scrollTarget.scrollHeight;
      });
    });
  }

  onMessageInput(): void {
    this.resizeMessageInput();

    if (!this.selectedThreadId || !this.currentUserId) {
      return;
    }

    this.chatService.setTyping(this.selectedThreadId, this.currentUserId, this.currentUserName, true);

    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }

    this.typingTimeout = setTimeout(() => {
      this.chatService.setTyping(this.selectedThreadId, this.currentUserId, this.currentUserName, false);
    }, 1200);
  }

  onMessageEnter(event: KeyboardEvent): void {
    if (event.shiftKey) {
      return;
    }

    event.preventDefault();

    if (!this.canCompose) {
      return;
    }

    this.sendMessage();
  }

  sendMessage(): void {
    this.submitted = true;
    const body = this.newMessage?.trim() || '';

    if ((!body && !this.selectedAttachment) || !this.currentUserId) {
      return;
    }

    if (this.selectedRecipientUserIds.length > 0) {
      this.chatService
        .createOrGetThreadForParticipants(this.currentUserId, this.selectedRecipientUserIds)
        .pipe(takeUntil(this.destroy$))
        .subscribe((thread) => {
          if (!thread?.id) {
            return;
          }

          this.isCreatingNewConversation = false;
          this.selectedRecipientUserIds = [];
          this.openThread(thread.id);
          this.sendMessageToThread(thread.id, body);
        });

      return;
    }

    if (!this.selectedThreadId) {
      return;
    }

    this.sendMessageToThread(this.selectedThreadId, body);
  }

  deleteSelectedThread(): void {
    if (!this.selectedThreadId || !this.currentUserId || this.deletingThread) {
      return;
    }

    this.confirmDialogService.confirmThis(
      ['Delete this conversation?', 'This cannot be undone.'],
      () => this.confirmDeleteSelectedThread(),
      () => {
      }
    );
  }

  toggleEmojiPicker(): void {
    this.showEmojiPicker = !this.showEmojiPicker;
  }

  addEmoji(emoji: string): void {
    this.newMessage = `${this.newMessage || ''}${emoji}`;
    this.showEmojiPicker = false;
    this.focusMessageInput();
  }

  onAttachmentSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0] || null;

    this.attachmentError = '';
    this.attachmentErrorFileName = '';
    this.selectedAttachment = null;

    if (!file) {
      return;
    }

    if (file.size > this.maxAttachmentSizeBytes) {
      this.attachmentError = 'Attachment must be 5 MB or smaller.';
      this.attachmentErrorFileName = file.name;
      input.value = '';
      this.focusMessageInput();
      return;
    }

    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    if (extension && this.blockedAttachmentExtensions.has(extension)) {
      this.attachmentError = 'Executable file types are not allowed.';
      this.attachmentErrorFileName = file.name;
      input.value = '';
      this.focusMessageInput();
      return;
    }

    this.selectedAttachment = file;
    this.focusMessageInput();
  }

  clearAttachment(): void {
    this.selectedAttachment = null;
    this.attachmentError = '';
    this.attachmentErrorFileName = '';

    if (this.attachmentInput?.nativeElement) {
      this.attachmentInput.nativeElement.value = '';
    }
  }

  private focusMessageInput(): void {
    setTimeout(() => {
      this.messageInput?.nativeElement?.focus();
    }, 0);
  }

  private confirmDeleteSelectedThread(): void {
    if (!this.selectedThreadId || !this.currentUserId || this.deletingThread) {
      return;
    }

    const threadId = this.selectedThreadId;
    this.deletingThread = true;

    this.chatService
      .deleteThread(threadId, this.currentUserId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (deleted) => {
          if (!deleted) {
            return;
          }

          this.selectedThreadId = '';
          this.selectedMessages = [];
          this.newMessage = '';
          this.submitted = false;
          this.selectedRecipientUserIds = [];
          this.router.navigate(['/chat']).then();
        },
        error: () => {
          this.deletingThread = false;
        },
        complete: () => {
          this.deletingThread = false;
        }
      });
  }

  private sendMessageToThread(threadId: string, body: string): void {
    if (!this.isValidThreadId(threadId) || !this.currentUserId) {
      return;
    }

    this.chatService
      .sendMessage(threadId, this.currentUserId, body, this.selectedAttachment)
      .pipe(takeUntil(this.destroy$))
      .subscribe((created) => {
        this.chatService.appendMessage(created);
        this.newMessage = '';
        this.resetMessageInputHeight();
        this.clearAttachment();
        this.submitted = false;
        this.chatService.setTyping(threadId, this.currentUserId, this.currentUserName, false);
      });
  }

  private resizeMessageInput(): void {
    const textarea = this.messageInput?.nativeElement;
    if (!textarea) {
      return;
    }

    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }

  private resetMessageInputHeight(): void {
    const textarea = this.messageInput?.nativeElement;
    if (!textarea) {
      return;
    }

    textarea.style.height = 'auto';
  }

  private loadAvailableUsers(): void {
    if (!this.currentUserId) {
      this.availableUsers = [];
      return;
    }

    this.chatService
      .loadUsers(this.currentUserId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((users) => {
        const loadedUsers = (users || [])
          .filter((u) => !!u.userId)
          .map((u) => ({
            ...u,
            name: u.name || u.userId
          }));

        const uniqueUsers = loadedUsers.filter((user, index, arr) =>
          arr.findIndex((candidate) => candidate.userId === user.userId) === index
        );

        this.availableUsers = uniqueUsers.sort((a, b) => a.name.localeCompare(b.name));
      });
  }

  private isValidThreadId(threadId: string | null | undefined): threadId is string {
    if (!threadId) {
      return false;
    }

    const normalized = threadId.toString().trim().toLowerCase();
    return normalized !== '' && normalized !== 'undefined' && normalized !== 'null';
  }

  isOwnMessage(message: ChatMessage): boolean {
    return message.senderUserId === this.currentUserId;
  }

  isMessageReadByOthers(message: ChatMessage): boolean {
    return message.readByUserIds.some((id) => id !== this.currentUserId);
  }

  formatTime(value: string): string {
    if (!value) {
      return '';
    }

    return new Date(value).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
  }

  formatDateTime(value: string): string {
    if (!value) {
      return '';
    }

    return new Date(value).toLocaleString();
  }

  resolveAttachmentUrl(attachmentUrl?: string): string {
    return this.chatService.resolveAttachmentUrl(attachmentUrl);
  }

  onAttachmentClick(message: ChatMessage, event: MouseEvent): void {
    if (!message.attachmentUrl) {
      return;
    }

    event.preventDefault();

    this.chatService.downloadAttachment(message.attachmentUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe((blob) => {
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = message.attachmentFileName || 'attachment';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
      });
  }

  async ngOnDestroy(): Promise<void> {
    if (this.shouldTeardownOnDestroy()) {
      await this.teardownRealtime();
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  private async teardownRealtime(): Promise<void> {
    // console.log('[Chat][Component] Teardown realtime started');
    // Important: we don't want to trigger a hub call for setActiveThread('')
    // because we are about to disconnect the hub connection entirely.
    // The current thread group membership will be cleaned up by the server when we disconnect.
    await this.chatService.disconnectRealtime();

    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = null;
    }
  }

  private shouldTeardownOnDestroy(): boolean {
    const currentNavigation = this.router.currentNavigation();
    const nextUrl = currentNavigation?.finalUrl?.toString()
      || currentNavigation?.extractedUrl?.toString()
      || '';

    if (!nextUrl) {
      return false;
    }

    return !nextUrl.startsWith('/chat');
  }

  private updateTabTitle(): void {
    const threadTitle = this.selectedThread?.title?.trim();
    this.titleService.setTitle(threadTitle ? `Chat - ${threadTitle}` : 'Chat');
  }
}
